import React from 'react';
import { Cancel as CancelIcon, ParkSale, RecallParkSale, Process as ProcessIcon, QuoteIcon } from '@markinson/uicomponents-v2/SvgIcons';
import FastEntry from '../../common/FastEntry';
import { DataGrid } from 'devextreme-react';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Paper, withStyles } from '@material-ui/core';
import styles from './Lines.styles';
import { ILinesHandle, ILinesProperties } from './Lines.properties';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns } from './constants';
import { hasFormsInResponseData, isScreenRequest } from 'api/utils';
import { IFastEntryHandle } from 'components/common/FastEntry/FastEntry.properties';
import { IProductReceiptGridLineFacade } from 'api/swaggerTypes';
import { usePRContextSelector } from '../ProductReceipts.context';
import { HotKeyNames } from 'utils/constants';
import { useCalculateFastLine, useCreateFastLine, useCalculateProductReceiptLine, useDeleteProductReceiptLine, useRetrieveProductReceiptLines, useRetrieveProductReceiptSummary, useUpdateProductReceiptLine } from 'api/productReceipts/productReceipts';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { isNull } from 'utils/utils';

const RecallReceiptIcon = (itemProps) => <RecallParkSale {...itemProps} style={{ width: 35, height: 35, margin: 3 }} />;
const ParkReceiptIcon = (itemProps) => <ParkSale {...itemProps} style={{ width: 35, height: 35, margin: 3 }} />;
const FocusTimeout = 200;

const Lines = (props: ILinesProperties, ref: React.Ref<ILinesHandle>) => {
    const { classes, changeSelectedTab } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const [linesCount, setLinesCount] = React.useState(0);
    const dataGridInnerRef = React.useRef<DataGrid>();
    const FastEntryRef = React.useRef<IFastEntryHandle>(null);
    const PRSummary = usePRContextSelector<'SummaryPanelData'>((state) => state.SummaryPanelData);
    const ProductReceiptId = usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt);
    const fetchLinesMutation = useRetrieveProductReceiptLines();
    const { mutateAsync: calculateFastLineMutateAsync } = useCalculateFastLine();
    const { mutateAsync: createFastLineMutateAsync } = useCreateFastLine();
    const { refetch: refetchProductReceiptSummary } = useRetrieveProductReceiptSummary(ProductReceiptId);
    const calculateLineMutation = useCalculateProductReceiptLine();
    const updateLineMutation = useUpdateProductReceiptLine();
    const deleteLineMutation = useDeleteProductReceiptLine();
    const fetchPRSummary = useRetrieveProductReceiptSummary(usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt));
    const [errorMessages, setErrorMessages] = React.useState<any>([]);
    const [refreshData, setRefreshData] = React.useState<boolean>(false);

    const centerIcons = [
        {
            label: 'Recall Receipt',
            Icon: RecallReceiptIcon,
            action: 'RecallReceipt',
            disabled: ProductReceiptId > 0,
            hotkey: HotKeyNames.CtrlR
        },
        {
            label: 'Load',
            Icon: QuoteIcon,
            action: 'Load',
            disabled: ProductReceiptId === 0,
            hotkey: HotKeyNames.CtrlL
        }
    ];

    const rightIcons = [
        {
            label: 'Park',
            Icon: ParkReceiptIcon,
            action: 'ParkReceipt',
            disabled: ProductReceiptId === 0 || linesCount === 0,
            hotkey: HotKeyNames.CtrlP
        },
        {
            label: 'Cancel',
            Icon: CancelIcon,
            action: 'CancelReceipt',
            iconStyle: ProductReceiptId === 0 ? undefined : { fill: 'rgba(178, 0, 0, 1)' },
            disabled: ProductReceiptId === 0,
            hotkey: HotKeyNames.CtrlBackspace
        },
        {
            label: 'Process',
            Icon: ProcessIcon,
            action: 'Process',
            iconStyle: (ProductReceiptId === 0 || linesCount === 0) ? undefined : { fill: 'rgba(109, 217, 0, 1)' },
            disabled: ProductReceiptId === 0 || linesCount === 0,
            hotkey: HotKeyNames.CtrlEnter
        }
    ];

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons,
                rightIcons
            });
        },
        [ProductReceiptId, linesCount, PRSummary]
    );
    React.useEffect(
        () => {
            if (refreshData) {
                dataGridInnerRef.current?.instance?.refresh();
                dataGridInnerRef.current?.instance?.cancelEditData();
                setRefreshData(false);
            }

        },
        [refreshData]
    );

    const refreshDataGrid = React.useCallback(
        async (messages?: any) => {
            setRefreshData(true);
            if (messages) {
                setErrorMessages(messages);
            }

        },
        [setErrorMessages, setRefreshData]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            refreshDataGrid,
            fastEntryResetToDefault: () => {
                FastEntryRef?.current?.resetToDefault();
            }
        }),
        [refreshDataGrid, FastEntryRef]
    );

    const fetchLines = React.useCallback(
        async (BatchPage, BatchSize, Sort) => {
            if (ProductReceiptId !== undefined && ProductReceiptId > 0) {
                try {
                    const lines = await fetchLinesMutation.mutateAsync({
                        ProductReceiptId: ProductReceiptId,
                        urlQueryParams: {
                            BatchPage,
                            BatchSize,
                            Sort
                        }
                    });

                    setLinesCount(lines?.ProductReceiptGridLine?.length ?? 0);

                    if (lines && lines?.ProductReceiptGridLine && errorMessages && errorMessages.length > 0) {
                        errorMessages?.forEach((message) => {
                            if (message.MessageFile === 'BinLot' && message.MessageKey) {
                                lines?.ProductReceiptGridLine?.filter((line: any) => line?.LineNumber === parseInt(message.MessageKey))?.forEach((matchedLine) => { (matchedLine as any).AlertMessage = message.MessageDetail; });
                            }
                        });
                    }

                    return lines?.ProductReceiptGridLine ?? [];
                } catch (error) {
                    console.error('Error fetching lines:', error);

                    return null;
                }
            } else {
                return null;
            }
        },
        [ProductReceiptId, errorMessages]
    );

    const refreshProductReceiptslines = async (response) => {
        if (response?.Status && !isScreenRequest(response?.Forms)) {
            await linesDataSource.reload();
            dataGridInnerRef.current.instance.refresh(true);
            FastEntryRef.current.resetToDefault();
            refetchProductReceiptSummary();

            return null;
        }

        return response;
    };
    async function calculateProductReceiptLine(line: IProductReceiptGridLineFacade, field: keyof IProductReceiptGridLineFacade): Promise<any> {
        const response = await calculateLineMutation.mutateAsync({
            ProductReceiptGridLine: line,
            ProductReceiptId: ProductReceiptId,
            LineNumber: line?.LineNumber,
            urlQueryParams: {
                ChangedField: field,
            }
        });

        if (response?.Status) {
            if (!isNull(response?.ProductReceiptGridLine?.inlineObject)) {
                return { line: response.ProductReceiptGridLine?.inlineObject, status: response.Status };
            } else {
                return { line, status: response.Status };
            }
        } else {
            return { line: {}, status: false };
        }
    }

    async function updateProductReceiptLine(data: IProductReceiptGridLineFacade): Promise<any> {
        const response = await updateLineMutation.mutateAsync({
            ProductReceiptGridLine: data,
            ProductReceiptId: ProductReceiptId,
            LineNumber: data?.LineNumber
        });

        if (response?.Status) {
            refreshDataGrid();
            fetchPRSummary.refetch();
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    }

    const deleteLine = React.useCallback(
        async (lineNumber: number) => {
            if (lineNumber && lineNumber !== 0 && ProductReceiptId) {
                const response = await deleteLineMutation.mutateAsync({
                    ProductReceiptId: ProductReceiptId,
                    LineNumber: lineNumber
                });

                if (response?.Status) {
                    refreshDataGrid();
                    fetchPRSummary.refetch();
                }
            }
        },
        [ProductReceiptId]
    );

    const linesDataSource = React.useMemo(
        () => createDataSource(
            'LineNumber',
            { fetch: fetchLines, update: updateProductReceiptLine },
        ),
        [fetchLines]
    );

    const focusFastEntryPurchaseField = () => {
        setTimeout(
            () => {
                FastEntryRef?.current?.focusPurchaseField();
            },
            FocusTimeout
        );
    };

    const onApply = async ({ DiscountedPrice: BuyPrice, ProductId, PurchaseOrder, LineNumber: PurchaseOrderLineNumber, OrderedQuantity: Quantity }: any) => {
        const data = {
            BuyPrice,
            ProductId,
            ProductReceiptId,
            PurchaseOrder,
            PurchaseOrderLineNumber,
            Quantity
        };
        const response = await createFastLineMutateAsync({
            POOnfile: true,
            ProductReceiptId,
            ProductReceiptFastLine: data
        });

        if (hasFormsInResponseData(response)) {
            const { Message } = response.Forms[0];
            if (Message.includes('PurchaseOrder')) focusFastEntryPurchaseField();
        }

        return refreshProductReceiptslines(response);
    };

    const onFastEntryFieldChanged = async ({ BuyPrice, ProductId, PurchaseOrder, LineNumber: PurchaseOrderLineNumber, OrderedQuantity: Quantity, field: ChangedField }: any) => {
        const data = {
            BuyPrice,
            ProductId,
            ProductReceiptId,
            PurchaseOrder,
            PurchaseOrderLineNumber,
            Quantity
        };
        const result = await calculateFastLineMutateAsync({
            POOnfile: true,
            ProductReceiptFastLine: data,
            ProductReceiptId: ProductReceiptId || 0,
            urlQueryParams: {
                ChangedField
            }
        });

        return {
            Status: result?.Status,
            FastLineDetails: {
                ...result,
                ...result?.ProductReceiptFastLine,
                DiscountedPrice: result?.ProductReceiptFastLine?.BuyPrice,
                OrderedQuantity: result?.ProductReceiptFastLine?.Quantity,
            }
        };
    };

    const detailOnClick = React.useCallback(
        ({ data }) => {
            if (!data?.DisableLine) {
                changeSelectedTab('LineDetails', {
                    search: {
                        ProductReceipt: ProductReceiptId,
                        LineNumber: data?.LineNumber
                    }
                });
            }
        },
        [changeSelectedTab, ProductReceiptId]
    );

    const onEditorPreparing = (e) => {
        if (e.dataField === 'Description') {
            e.editorOptions.disabled = !e.row.data.EnableDescription;
        }
    };

    return (
        <div className={classes.parentContainer}>
            <Paper className={classes.fastEntryContainer}>
                <FastEntry
                    innerRef={FastEntryRef}
                    config={{
                        priceFieldAsLookup: false,
                        disableFastEntry: !ProductReceiptId,
                        module: 'productReceipts',
                        params: { Supplier: Number(PRSummary?.SupplierId), Warehouse: PRSummary?.Warehouse }
                    }}
                    handlers={{
                        onApply,
                        onFastEntryFieldChanged
                    }}

                />
            </Paper>
            <Paper className={classes.gridContainer}>
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    calculateFieldValue={calculateProductReceiptLine}
                    allowUpdating={true}
                    detailOnClick={detailOnClick}
                    gridProps={{
                        dataSource: linesDataSource,
                        dataGridRef: dataGridInnerRef,
                        sorting: { mode: 'single' },
                        disableDrillDown: false,
                        disableEditing: false,
                        disableDelete: false,
                        onEditorPreparing: onEditorPreparing,
                    }}
                    onDelete={deleteLine}
                />
            </Paper>
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Lines)));
